import React from 'react';
import { ColorScheme } from '../types/settings';
import type { PlatformType } from '../types/generation';
import raisoLogo from '../../assets/raiso-logo.png';

// ============================================================
// ENUMS — Source de vérité unique (ré-exportées par types/enums.ts)
// ============================================================

export enum LLMProvider {
  OPENAI = "openai",
  ANTHROPIC = "anthropic",
  OLLAMA = "ollama"
}

export enum UserRole {
  USER = "user",
  ADMIN = "admin",
  VIEWER = "viewer"
}

export enum ChannelCategory {
  EXTERNAL = "external",
  INTERNAL = "internal"
}

// Values match backend ChannelType enum (lowercase)
export enum ChannelType {
  LINKEDIN = "linkedin",
  TWITTER = "twitter",
  FACEBOOK = "facebook",
  INSTAGRAM = "instagram",
  TIKTOK = "tiktok",
  BAIDU = "baidu",
  PINTEREST = "pinterest",
  YOUTUBE = "youtube",
  REDDIT = "reddit",
  GOOGLE_BUSINESS = "googlebusiness",
  NEWSLETTER = "newsletter",
  SLACK = "slack",
  WHATSAPP = "whatsapp",
  TELEGRAM = "telegram",
  TEAMS = "teams",
  DISCORD = "discord",
  THREADS = "threads",
  BLUESKY = "bluesky",
  OTHER = "other"
}

export enum SourceType {
  RSS = "rss",
  YOUTUBE = "youtube",
  WEBSITE = "website",
  PODCAST = "podcast",
  RAW_TEXT = "raw_text"
}

export enum PostStatus {
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  PUBLISHED = "published",
  FAILED = "failed",
  PENDING = "pending"
}

/**
 * Valeurs possibles du dropdown "Statut" dans la galerie de posts.
 *
 * On ne réutilise pas directement `PostStatus` car :
 *   - `ALL` n'est pas un statut métier mais un filtre "ne rien filtrer".
 *   - `DRAFT` côté filtre regroupe aussi les anciens posts sans statut explicite
 *     (PENDING ou status absent + pas de scheduledDate) — c'est plus large que
 *     `PostStatus.DRAFT` strictement.
 *
 * Les valeurs string sont alignées avec `PostStatus.*` pour faciliter le
 * mapping dans la logique de filtrage.
 */
export enum PostStatusFilter {
  ALL = "all",
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  PUBLISHED = "published",
}

/**
 * Types de sources renvoyés par l'endpoint IA `POST /sources/suggest`.
 *
 * Pourquoi un enum dédié (et pas `SourceType` ci-dessus) ?
 *   - `SourceType` est l'enum de persistence (RSS / YOUTUBE / WEBSITE / PODCAST / RAW_TEXT).
 *   - Pour les suggestions IA, on n'expose pas YouTube ni Podcast (le LLM ne peut pas
 *     inventer un channel ID YouTube valide ni un flux Podcast accessible).
 *   - On accepte par contre NEWSLETTER comme catégorie de suggestion — qui n'a pas
 *     d'équivalent direct côté `SourceType` (les newsletters sont stockées en WEBSITE).
 *
 * Le mapping vers `SourceType` se fait au moment de l'insertion (cf SuggestSourcesModal).
 */
export enum SuggestedSourceType {
  RSS = "rss",
  NEWSLETTER = "newsletter",
  WEBSITE = "website",
}

export enum NewsletterStatus {
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  SENT = "sent"
}

export enum ChatRole {
  SYSTEM = "system",
  USER = "user",
  ASSISTANT = "assistant"
}

// ============================================================
// SIDEBAR
// ============================================================

export enum SidebarMode {
  EXPANDED = 'expanded',
  COLLAPSED = 'collapsed',
}

export const SIDEBAR_ITEMS = {
  SPACES: 'espaces',
  POSITIONING: 'positionnement',
  // Note (2026-05 chantier P.4) : BRAND_CONFIG a été retiré.
  // La charte graphique est désormais accessible via un 3e toggle dans PositioningTab
  // (cf BrandConfigContent.tsx), pas via un item Sidebar dédié.
  WATCH: 'veille',
  SOURCES: 'sources',
  CHANNELS: 'canaux',
  AUTOMATIONS: 'automatisations',
  NEWSLETTER: 'newsletter',
  // V4-ZERNIO-ANALYTICS — onglet analytics par space (cross-network).
  ANALYTICS: 'analytics',
  // V4-ZERNIO-COMMENTS — inbox des commentaires non répondus.
  ENGAGEMENT: 'engagement',
  // V4-ANALYTICS-HISTORY 2026-06-04 — "History" panel (right sidebar, post-publication).
  HISTORY: 'history',
  SETTINGS: 'settings',
  ACCOUNT: 'compte',
} as const;

// ============================================================
// LANGUAGES
// ============================================================

export const availableLanguages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇵' },
  { code: 'ar', name: 'العربية', flag: '🇦' },
];

// ============================================================
// CHANNEL ICONS — SVG imports
// ============================================================

import baiduSvg from '../../assets/svg/baidu.svg';
import blueskySvg from '../../assets/svg/bluesky.svg';
import discordSvg from '../../assets/svg/discord.svg';
import facebookSvg from '../../assets/svg/facebook.svg';
import instagramSvg from '../../assets/svg/instagram.svg';
import linkedinSvg from '../../assets/svg/linkedin.svg';
import microsoftSvg from '../../assets/svg/microsoft.svg';
import pinterestSvg from '../../assets/svg/pinterest.svg';
import redditSvg from '../../assets/svg/reddit.svg';
import slackSvg from '../../assets/svg/slack.svg';
import telegramSvg from '../../assets/svg/telegram.svg';
import threadsSvg from '../../assets/svg/threads.svg';
import tiktokSvg from '../../assets/svg/tiktok.svg';
import whatsappSvg from '../../assets/svg/whatsapp.svg';
import xSvg from '../../assets/svg/X.svg';
import youtubeSvg from '../../assets/svg/youtube.svg';
import googleBusinessSvg from '../../assets/svg/google_business.svg';

/** Mapping ChannelType -> chemin SVG (URL) */
export const channelSvgIcons: Record<string, string> = {
  [ChannelType.LINKEDIN]: linkedinSvg,
  [ChannelType.TWITTER]: xSvg,
  [ChannelType.FACEBOOK]: facebookSvg,
  [ChannelType.INSTAGRAM]: instagramSvg,
  [ChannelType.TIKTOK]: tiktokSvg,
  [ChannelType.YOUTUBE]: youtubeSvg,
  [ChannelType.PINTEREST]: pinterestSvg,
  [ChannelType.REDDIT]: redditSvg,
  [ChannelType.GOOGLE_BUSINESS]: googleBusinessSvg,
  [ChannelType.BAIDU]: baiduSvg,
  [ChannelType.SLACK]: slackSvg,
  [ChannelType.WHATSAPP]: whatsappSvg,
  [ChannelType.TELEGRAM]: telegramSvg,
  [ChannelType.TEAMS]: microsoftSvg,
  [ChannelType.DISCORD]: discordSvg,
  [ChannelType.THREADS]: threadsSvg,
  [ChannelType.BLUESKY]: blueskySvg,
  // Aliases pour compatibilité backend (noms avec majuscule)
  'Twitter': xSvg,
  'X': xSvg,
};

/** Fallback lettres pour les réseaux sans SVG */
export const channelIcons: Record<ChannelType | string, string> = {
  [ChannelType.TWITTER]: 'X',
  'Twitter': 'X',
  [ChannelType.FACEBOOK]: 'f',
  [ChannelType.INSTAGRAM]: 'IG',
  [ChannelType.LINKEDIN]: 'in',
  [ChannelType.NEWSLETTER]: '✉',
  [ChannelType.TIKTOK]: '♪',
  [ChannelType.YOUTUBE]: '▶',
  [ChannelType.PINTEREST]: 'P',
  [ChannelType.REDDIT]: 'R',
  [ChannelType.GOOGLE_BUSINESS]: 'G',
  [ChannelType.BAIDU]: 'B',
  [ChannelType.SLACK]: 'S',
  [ChannelType.WHATSAPP]: 'W',
  [ChannelType.TELEGRAM]: 'T',
  [ChannelType.TEAMS]: 'M',
  [ChannelType.DISCORD]: 'D',
  [ChannelType.THREADS]: '@',
  [ChannelType.BLUESKY]: '🦋',
};

/**
 * Composant réutilisable pour afficher le logo SVG d'un réseau social.
 * Affiche le SVG si disponible, sinon fallback sur la lettre dans un cercle coloré.
 */
interface ChannelSvgIconProps {
  network?: string;
  size?: number;
  className?: string;
  fallbackClassName?: string; // classes pour le cercle fallback (ex: 'bg-blue-700 text-white')
  fallbackStyle?: React.CSSProperties; // styles inline pour le cercle fallback
}

export function ChannelSvgIcon({ network, size = 20, className = '', fallbackClassName = '', fallbackStyle }: ChannelSvgIconProps) {
  const svgUrl = network ? channelSvgIcons[network] : undefined;

  if (svgUrl) {
    return (
      <img
        src={svgUrl}
        alt={network}
        width={size}
        height={size}
        className={`inline-block rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  // Fallback: lettre
  const letter = network ? (channelIcons[network] || network.charAt(0)) : '?';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold ${fallbackClassName} ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.45, ...fallbackStyle }}
    >
      {letter}
    </span>
  );
}

// ============================================================
// CHANNEL DISPLAY CONFIG — Source de vérité unique pour l'UI de chaque canal
// ============================================================

/** Configuration d'affichage pour chaque type de canal */
export interface ChannelDisplayConfig {
  label: string;
  color: string;     // Couleur « brand » du réseau
  bgColor: string;   // Fond léger / transparent
  category: 'external' | 'internal';  // Catégorie du canal (interne = messagerie, externe = réseaux sociaux)
}

/**
 * Mapping exhaustif ChannelType → config d'affichage.
 * Pour ajouter / modifier / supprimer un réseau, il suffit d'éditer ici.
 * La propriété `category` sert de source de vérité unique pour la classification interne/externe.
 */
export const CHANNEL_DISPLAY_CONFIG: Record<ChannelType, ChannelDisplayConfig> = {
  [ChannelType.TWITTER]:    { label: 'X',          color: '#000000', bgColor: 'rgba(0, 0, 0, 0.08)',           category: 'external' },
  [ChannelType.LINKEDIN]:   { label: 'LinkedIn',   color: '#0A66C2', bgColor: 'rgba(10, 102, 194, 0.1)',     category: 'external' },
  [ChannelType.FACEBOOK]:   { label: 'Facebook',   color: '#1877F2', bgColor: 'rgba(24, 119, 242, 0.1)',     category: 'external' },
  [ChannelType.INSTAGRAM]:  { label: 'Instagram',  color: '#E4405F', bgColor: 'rgba(228, 64, 95, 0.1)',      category: 'external' },
  [ChannelType.TIKTOK]:     { label: 'TikTok',     color: '#000000', bgColor: 'rgba(0, 0, 0, 0.08)',           category: 'external' },
  [ChannelType.YOUTUBE]:    { label: 'YouTube',    color: '#FF0000', bgColor: 'rgba(255, 0, 0, 0.1)',         category: 'external' },
  [ChannelType.PINTEREST]:  { label: 'Pinterest',  color: '#E60023', bgColor: 'rgba(230, 0, 35, 0.1)',        category: 'external' },
  [ChannelType.REDDIT]:     { label: 'Reddit',     color: '#FF4500', bgColor: 'rgba(255, 69, 0, 0.1)',        category: 'external' },
  [ChannelType.GOOGLE_BUSINESS]: { label: 'Google Business', color: '#4285F4', bgColor: 'rgba(66, 133, 244, 0.1)', category: 'external' },
  [ChannelType.BAIDU]:      { label: 'Baidu',      color: '#2319DC', bgColor: 'rgba(35, 25, 220, 0.1)',       category: 'external' },
  [ChannelType.NEWSLETTER]: { label: 'Newsletter', color: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.1)',    category: 'external' },
  [ChannelType.SLACK]:      { label: 'Slack',      color: '#4A154B', bgColor: 'rgba(74, 21, 75, 0.1)',        category: 'internal' },
  [ChannelType.WHATSAPP]:   { label: 'WhatsApp',   color: '#25D366', bgColor: 'rgba(37, 211, 102, 0.1)',     category: 'internal' },
  [ChannelType.TELEGRAM]:   { label: 'Telegram',   color: '#0088CC', bgColor: 'rgba(0, 136, 204, 0.1)',      category: 'internal' },
  [ChannelType.TEAMS]:      { label: 'Teams',      color: '#6264A7', bgColor: 'rgba(98, 100, 167, 0.1)',     category: 'internal' },
  [ChannelType.DISCORD]:    { label: 'Discord',    color: '#5865F2', bgColor: 'rgba(88, 101, 242, 0.1)',     category: 'internal' },
  [ChannelType.THREADS]:    { label: 'Threads',    color: '#000000', bgColor: 'rgba(0, 0, 0, 0.08)',           category: 'external' },
  [ChannelType.BLUESKY]:    { label: 'Bluesky',    color: '#0085FF', bgColor: 'rgba(0, 133, 255, 0.1)',      category: 'external' },
  [ChannelType.OTHER]:      { label: 'Autre',      color: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.1)',    category: 'external' },
};

/**
 * Retourne la catégorie d'un réseau (external/internal) depuis la config centralisée.
 * Source de vérité unique — utilisée par channelsService, usePostFilters, etc.
 */
export function getChannelCategory(network: string): ChannelCategory {
  const channelType = normalizeToChannelType(network);
  return CHANNEL_DISPLAY_CONFIG[channelType]?.category === 'internal'
    ? ChannelCategory.INTERNAL
    : ChannelCategory.EXTERNAL;
}

/**
 * Normalise un nom de réseau (toutes casses, alias) vers la valeur ChannelType.
 * Capitalise sur l'enum ChannelType pour rester exhaustif automatiquement.
 */
export function normalizeToChannelType(network: string): ChannelType {
  const lower = network.toLowerCase();
  // Match direct sur les valeurs de l'enum (déjà en lowercase)
  const directMatch = Object.values(ChannelType).find(v => v === lower);
  if (directMatch) return directMatch;
  // Alias pour les cas particuliers
  const aliases: Record<string, ChannelType> = {
    'x': ChannelType.TWITTER,
    'microsoft teams': ChannelType.TEAMS,
  };
  return aliases[lower] || ChannelType.OTHER;
}

/**
 * Convertit un nom de réseau en PlatformType (pour l'API de génération backend).
 * Fallback sur 'linkedin' si le réseau n'est pas un PlatformType valide.
 */
const VALID_PLATFORM_TYPES: readonly string[] = ['linkedin', 'twitter', 'facebook', 'instagram', 'newsletter'] as const;

export function networkToPlatformType(network: string): PlatformType {
  const channelType = normalizeToChannelType(network) as string;
  return channelType as PlatformType;
  // if (VALID_PLATFORM_TYPES.includes(channelType)) {
  //   return channelType as PlatformType;
  // }
  // return 'linkedin';
}

/**
 * Retourne le ChannelDisplayConfig pour un nom de réseau quelconque.
 * Accepte n'importe quelle casse / alias.
 */
export function getChannelDisplayConfig(network: string): ChannelDisplayConfig {
  return CHANNEL_DISPLAY_CONFIG[normalizeToChannelType(network)];
}

// V4-ZERNIO-USERNAME-UNDEFINED (2026-07-15) : pour certains comptes LinkedIn, Zernio
// renvoie un `username` construit côté serveur à partir de firstName + lastName, et
// stringifie un lastName manquant en littéral JS "undefined" au lieu de l'omettre
// (ex: "Clément undefined"). Le backend nettoie désormais ce token à l'écriture, mais
// on défend aussi l'affichage pour les valeurs déjà persistées avant le fix.
const UNDEFINED_TOKEN_RE = /(?:^|\s)undefined(?:\s|$)/gi;

export function sanitizeZernioDisplayName(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const cleaned = raw.replace(UNDEFINED_TOKEN_RE, ' ').replace(/\s+/g, ' ').trim();
  return cleaned || undefined;
}

// ============================================================
// AGENT DISPLAY CONFIG — Pour l'UI de génération multi-agents
// ============================================================

export const AGENT_ICONS: Record<string, string> = {
  'knowledge': '📚',
  'writer': '✍️',
  'compliance': '✅',
  'positioning': '✅',
  'diversity': '🔀',
  'orchestrator': '🎯',
  'memory': '🧠',
  'critic': '✅',
  'system': '⚙️',
  'unknown': '🤖',
  // Legacy aliases
  'knowledge_agent': '📚',
  'writer_agent': '✍️',
  'compliance_agent': '✅',
};

const AGENT_DISPLAY_NAMES: Record<string, string> = {
  'knowledge': 'Agent Recherche',
  'writer': 'Agent Rédaction',
  'compliance': 'Agent Positionnement',
  'positioning': 'Agent Positionnement',
  'diversity': 'Agent Diversité',
  'orchestrator': 'Orchestrateur',
  'memory': 'Agent Mémoire',
  'critic': 'Agent Validation',
  'system': 'Système',
  'unknown': 'Agent',
  'knowledge_agent': 'Agent Recherche',
  'writer_agent': 'Agent Rédaction',
  'compliance_agent': 'Agent Positionnement',
};

export function getAgentDisplayName(agent: string): string {
  return AGENT_DISPLAY_NAMES[agent] || agent;
}

// ============================================================
// GENERATION STATUS CONFIG — Statuts du pipeline de génération
// ============================================================

export const GENERATION_STATUS_CONFIG: Record<string, { emoji: string; color: string }> = {
  pending:    { emoji: '⏳', color: 'var(--color-text-secondary)' },
  generating: { emoji: '🔄', color: 'var(--color-primary)' },
  completed:  { emoji: '✅', color: 'var(--color-success)' },
  error:      { emoji: '❌', color: 'var(--color-danger)' },
};

// ============================================================
// TIMEZONES
// ============================================================

export const TIMEZONES = [
  { offset: '+1', name: 'Europe/Paris' },
  { offset: '+0', name: 'Europe/London' },
  { offset: '-5', name: 'America/New_York' },
  { offset: '-8', name: 'America/Los_Angeles' },
  { offset: '+9', name: 'Asia/Tokyo' },
  { offset: '+8', name: 'Asia/Shanghai' },
  { offset: '+10', name: 'Australia/Sydney' },
];

// ============================================================
// COLOR SCHEMES
// ============================================================

export const MAIN_COLOR_KEYS: Array<keyof ColorScheme> = ['primary', 'background', 'surface', 'border', 'textPrimary', 'textSecondary', 'textLight', 'accent'];
export const ACCENT_COLOR_KEYS: Array<keyof ColorScheme> = ['success', 'warning', 'danger'];

/**
 * Z-index scale documentée (UX backlog 11.2).
 *
 * Pourquoi
 * --------
 * Plusieurs composants avaient des `z-[30000]` hard-codés en classe Tailwind
 * (modales) et en `style={{zIndex: 9999}}` (tooltips Sidebar), avec risque de
 * conflits visuels. Cette scale fixe l'ordre attendu, à utiliser en `style.zIndex`
 * (les classes Tailwind jit-compiled gardent leurs valeurs littérales pour
 * compat — les CSS values arbitraires marchent toujours).
 *
 * Usage : `style={{ zIndex: Z_INDEX.DIALOG }}` partout où on rend en portal.
 */
export const Z_INDEX = {
  DROPDOWN: 20000,
  DIALOG: 30000,
  TOAST: 40000,
  TOOLTIP: 50000, // au-dessus des dialogs pour rester lisible
} as const;

// Timeouts WS génération — partagés par useGeneration / useRegeneratePost /
// useGenerateFromVisual (V4-GEN-WS-TIMEOUT-VS-LLM-LATENCY).
// Le backend émet un heartbeat toutes les 45s pendant la génération : l'inactivité
// de 5 min ne peut donc se déclencher que si le backend est réellement mort.
export const WS_INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;
// Plafond absolu anti-génération éternelle (jamais ré-armé).
export const WS_GENERATION_HARDCAP_MS = 10 * 60 * 1000;

// Timeout HTTP des appels de génération longs (LLM / image). Le défaut axios
// d'apiClient (30 s) coupait `/generation/generate-batch` alors que le backend
// continuait et persistait le post (« aucun post généré » puis post fantôme).
export const GENERATION_HTTP_TIMEOUT_MS = 240 * 1000;
// `/sources/suggest` (LLM + validation des URLs) prend jusqu'à ~120 s côté backend.
export const SOURCES_SUGGEST_TIMEOUT_MS = 150 * 1000;
// Après un batch de génération en échec ou partiel, le backend peut encore
// persister des posts : on recharge la liste une fois tout de suite, puis une
// seconde fois après ce délai (deux appels fixes, jamais de boucle).
export const GENERATION_LATE_POSTS_REFRESH_DELAY_MS = 30 * 1000;

// Une source est « périmée » (badge d'alerte de SourcesTab) au-delà de ce délai
// sans synchronisation.
export const SOURCE_STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

/** Valeurs de `sync_status` d'une source (miroir de `Constants.SYNC_STATUS_*` backend). */
export enum SourceSyncStatus {
  PENDING = "pending",
  SYNCING = "syncing",
  SUCCESS = "success",
  FAILED = "failed",
}

export const COLOR_PRESETS = {
  light: {
    primary: '#5500FF',
    background: '#F8F9FA',
    surface: '#FFFFFF',
    border: '#E5E7EB',
    textPrimary: '#1F2937',
    textSecondary: '#6B7280',
    textLight: '#FFFFFF',
    accent: '#333333',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
  },
  dark: {
    primary: '#8B5CF6',
    background: '#0F172A',
    surface: '#1E293B',
    border: '#334155',
    textPrimary: '#F1F5F9',
    textSecondary: '#94A3B8',
    textLight: '#FFFFFF',
    accent: '#CCCCCC',
    success: '#34D399',
    warning: '#FBBF24',
    danger: '#F87171',
  },
  debug: {
    primary: '#FF0000',
    background: '#FFFF00',
    surface: '#00FFFF',
    border: '#FF00FF',
    textPrimary: '#000000',
    textSecondary: '#0000FF',
    textLight: '#FFFFFF',
    accent: '#8B4513',
    success: '#00FF00',
    warning: '#FFA500',
    danger: '#8B00FF',
  },
} as const;

// ============================================================
// SOURCE TYPE COLORS
// ============================================================

export const SOURCE_TYPE_COLORS = {
  'youtube': '#dc2626',
  'podcast': '#9333ea',
  'website': '#2563eb',
  'rss': '#f97316',
  'raw_text': '#059669',
} as const;

// ============================================================
// CHANNEL COLOR MAP
// ============================================================

export const CHANNEL_COLOR_MAP: Record<string, string> = {
  // Social Media - External Channels
  'bg-accent': '#333333',
  'bg-blue-600': '#2563eb',
  'bg-pink-500': '#ec4899',
  'bg-blue-700': '#1d4ed8',
  'bg-purple-600': '#9333ea',
  'bg-slate-900': '#0f172a',
  'bg-red-600': '#dc2626',
  'bg-red-500': '#ef4444',
  'bg-blue-800': '#1e40af',
  // Messaging - Internal Channels
  'bg-purple-700': '#7e22ce',
  'bg-green-500': '#22c55e',
  'bg-blue-500': '#3b82f6',
  'bg-indigo-600': '#4f46e5',
  'bg-indigo-500': '#6366f1',
};

// ============================================================
// ZERNIO — Connexion réseaux sociaux
// ============================================================

/**
 * Plateformes nécessitant une sélection secondaire après OAuth (mode headless zernio).
 * Après le redirect OAuth, zernio retourne un connect_token au lieu de créer directement
 * le compte. L'utilisateur choisit ensuite une page/org/board/location via AccountSelectionModal.
 *
 * LinkedIn    → sélection d'organisation
 * Facebook    → sélection de page
 * Pinterest   → sélection de tableau
 * Google Business → sélection d'établissement
 */
export const ZERNIO_HEADLESS_PLATFORMS: readonly string[] = [
  'linkedin',
  'facebook',
  'pinterest',
  'googlebusiness',
] as const;

// ============================================================
// DEFAULTS
// ============================================================

export const DEFAULT_LOGO_URL = raisoLogo;

export const DEFAULT_AUTOMATION_SETTINGS = {
  watch: {
    enabled: false,
    frequencyDays: 7,
    periodDays: 7,
    preferredHour: 2,
  },
  generation: {
    enabled: false,
    channelIds: [],
    frequencyDays: 7,
    postsPerIteration: 5,
  },
  publication: {
    enabled: false,
    channelIds: [],
    frequencyDays: 7,
    postsPerIteration: 3,
  },
};

// V4-NEWSLETTER-TEMPLATES-FR-ONLY-DUPLICATED 2026-07-30 — `DEFAULT_NEWSLETTER_TEMPLATE`
// a été retiré d'ici : c'était le 4ᵉ gabarit d'email newsletter en français en dur.
// La graine de l'éditeur est maintenant construite par `buildNewsletterHtml`
// (src/utils/newsletterUtils.ts) avec la copie traduite, depuis `useSettings`.
// Ne pas réintroduire un gabarit littéral dans ce fichier.
